import React, { useState } from 'react'
import PropTypes from 'prop-types'
import toastr from 'toastr'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'

import Http from '@shared/http'
import { Modal, ModalHeader, ModalSubHeader, ModalContent } from '../index'
import { DownloadingPdfModal } from '../DownloadingPdfModal'
import { InputFieldWithLabel } from '../../Form/Fields'
import { GenericErrorModal } from '../GenericErrorModal'
import SectionSelect from '../../Form/SectionSelect'
import { Button } from '../../Button'
import './configurePdfDownloadModal.scss'

const defaultBlocks = [
  { label: "Assets", name: "assets" },
  { label: "Liabilities", name: "liabilities" },
  { label: "Budget", name: "budget" },
  { label: "Cashflow events", name: "cashflow_events" },
  { label: "Capital chart", name: "capital_chart" },
  { label: "Income chart", name: "income_chart" },
  { label: "Details", name: "details" },
]
const optionalBlocks = [
  { label: "Notes", name: "notes" },
  { label: "Risk profile", name: "risk_profile" },
  { label: "Risk products", name: "risk_products" },
  { label: "Asset allocation", name: "asset_allocation" },
  { label: "Financial needs analysis", name: "financial_needs_analysis" },
]

const blocks = [...defaultBlocks, ...optionalBlocks]

const baseClass = 'configure-pdf-download-modal'

const ConfigurePdfDownloadModal = ({
  open,
  onClose,
  scenarioId, // number
  isEmail = false,
  paths /* { notifyCompliancePath, pdfReportPath } */,
  usePresentValue
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [errorSubmittingSendToCrm, setErrorSubmittingSendToCrm] = useState(false)

  const onSubmit = async (values) => {
    setSubmitting(true);
    const excludeBlocks = blocks.reduce((acc, block) => {
      if (values[block.name] == false) {
        acc.push(block.name);
      }
      return acc;
    }, []);
    const emailValues = {
      name: values['name'],
      email: values['email'],
      additional_name: values['additional_name'],
      additional_email: values['additional_email'],
    }
    if (isEmail) {
      try {
        const params = {
          retirement_scenario_id: scenarioId,
          notify_compliance: emailValues,
          use_present_value: usePresentValue,
          exclude_blocks: excludeBlocks
        }
        const response = await Http.request(paths.notifyCompliancePath, 'POST', JSON.stringify(params))
        if (response.ok) {
          toastr.success('Successfully sent retirement plan details')
          onClose()
        } else {
          setErrorSubmittingSendToCrm(true)
        }
      } catch (e) {
        setErrorSubmittingSendToCrm(true)
      }
    } else /* isDownload */ {
      setDownloading(true)
      const params = new URLSearchParams({
        retirement_scenario_id: scenarioId,
        use_present_value: usePresentValue,
        exclude_blocks: excludeBlocks
      })
      window.location.replace(`${paths.pdfReportPath}?${params}`)
    }

    setSubmitting(false);
  }

  const prefillBlocks = (blockArray, value) => blockArray.reduce((acc, block) => ({
    ...acc,
    [block.name]: value
  }), {})

  if (errorSubmittingSendToCrm) {
    return (<GenericErrorModal open={open} close={onClose} />)
  }

  if (!isEmail && downloading) {
    return (<DownloadingPdfModal open={downloading} close={() => { onClose(); setDownloading(false); }} />)
  }

  const validationSchema = isEmail && Yup.object().shape({
    name: Yup.string()
      .required('A name for the recipient is required.'),
    email: Yup.string()
      .email(`This doesn't look like a valid email.`)
      .required('An email is required.'),
    additional_email: Yup.string()
      .email(`This doesn't look like a valid email.`)
  })

  return (
    <Modal open={open} onClose={onClose}>
      <ModalHeader>PDF Report</ModalHeader>
      <ModalSubHeader>
        {isEmail ?
          "Enter an email address to send a PDF of the current plan"
          : "Select the sections that you would to like see in your report."
        }
      </ModalSubHeader>
      <ModalContent>
        <Formik
          initialValues={{
            ...prefillBlocks(defaultBlocks, true),
            ...prefillBlocks(optionalBlocks, true),
            name: '',
            email: '',
            additional_name: '',
            additional_email: ''
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => onSubmit(values)}
        >
          {(props) => (
            <FormikForm className={`app-form ${baseClass}`}>
              {isEmail && (
                <div className={`${baseClass}__email-section`}>

                  <h5 style={{ margin: '0' }}>Recipient details:</h5>
                  <div className={`${baseClass}__flex-container`}>
                    <InputFieldWithLabel name='name' label='Name' />
                    <InputFieldWithLabel name='email' label='Email' />
                  </div>

                  <h5 style={{ margin: '0' }}>CC compliance or someone else:</h5>
                  <div className={`${baseClass}__flex-container`}>
                    <InputFieldWithLabel name='additional_name' label='Name' />
                    <InputFieldWithLabel name='additional_email' label='Email' />
                  </div>

                </div>
              )}
              <div className={`${baseClass}__download-section`}>

                <SectionSelect sections={[
                  {
                    title: "Sections",
                    description: "Select the sections you wish to see in the report",
                    items: defaultBlocks
                  },
                  {
                    title: "",
                    description: "",
                    items: optionalBlocks
                  }
                ]}/>

                <div className={`${baseClass}__flex-container`}>
                  <div className={`${baseClass}__selection-actions`}>
                    <a onClick={(e) => {
                      e.preventDefault();
                      props.setValues({ ...props.values, ...prefillBlocks(blocks, false)});
                    }}>Clear all</a>
                    <a onClick={(e) => {
                      e.preventDefault();
                      props.setValues({ ...props.values, ...prefillBlocks(blocks, true)});
                    }}>Select all</a>
                  </div>
                </div>
              </div>

              <div className={`${baseClass}__actions`}>
                <Button secondary fullWidth onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" fullWidth primary disabled={submitting}>
                  {isEmail ?
                    (submitting ? 'Sending PDF report...' : 'Send')
                    : (submitting ? 'Downloading PDF report...' : 'Download')
                  }
                </Button>
              </div>
              <div className={`${baseClass}__disclaimer`}>
                <p>The PDF report is based on the time value you have selected on the dashboard.</p>
                <p>If you selected to view the dashboad in PV, the report will also be in PV.</p>
              </div>
            </FormikForm>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

ConfigurePdfDownloadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isEmail: PropTypes.bool,
  paths: PropTypes.exact({
    notifyCompliancePath: PropTypes.string,
    pdfReportPath: PropTypes.string
  }).isRequired,
  usePresentValue: PropTypes.bool
}

export default ConfigurePdfDownloadModal
